import { useState } from "react";
import { EVENT_TYPE_CATEGORY_MAP, type EntityType, type EventOption, type EventType } from "../../types";
import { currentKind, kindLabel, kindsFor } from "../../config/stepKinds";
import { categoryOf, isDelayOption, removeOptionFromFlow, validateActivityFlow } from "../../utils/activityFlow";
import { START_STEP, insertStepAfter, setStepChoices, stepChoices } from "../../utils/stepOrder";
import { newOptionId } from "../../utils/newOptionId";
import { StepButtonsEditor } from "./StepButtonsEditor";
import "./StepOrderSheet.css";

const nameOf = (o: EventOption | undefined) => o?.displayName.trim() || "Untitled step";

/** What an edit touched: one point's buttons, or the steps themselves. */
export interface StepEdit {
  /** The point whose buttons changed (a step's id, or START_STEP). */
  stepId?: string;
  /** A step was renamed, added, removed or had its settings changed. */
  structural?: boolean;
}

interface StepFlowEditorProps {
  /** The steps as they run, flow and all. */
  options: EventOption[];
  onChange: (next: EventOption[], edit: StepEdit) => void;
  /** Which step to open first, if any (START_STEP for the start). */
  initialOpenId?: string | null;
  /** False where steps can't be added, renamed or removed. */
  canEditSteps?: boolean;
  /** The place's type, which decides the special kinds offered (food, flight, show). */
  entityType?: EntityType | string | null;
}

/**
 * The log steps as the flow they make: one row per step in the order the
 * activity runs, each showing the buttons that come after it. Tap a row to
 * open it: its name and kind, the buttons after it (preview, drag to order),
 * and the rarer settings. Used on the place and type pages, and in the sheet
 * behind "Edit buttons".
 */
export function StepFlowEditor({ options, onChange, initialOpenId = null, canEditSteps = true, entityType }: StepFlowEditorProps) {
  const [openId, setOpenId] = useState<string | null>(initialOpenId);
  const [adding, setAdding] = useState<{ name: string; eventType: EventType; after: string } | null>(null);

  const kinds = kindsFor(entityType, options);
  const byId = new Map(options.map((o) => [o.id, o] as const));
  const flowSteps = options.filter((o) => !isDelayOption(o));
  const delays = options.filter(isDelayOption);
  const issues = validateActivityFlow(options);
  const issuesFor = (id: string) => issues.filter((i) => i.optionId === id);

  const toggle = (id: string) => setOpenId((cur) => (cur === id ? null : id));
  const setButtons = (stepId: string, ids: string[]) => onChange(setStepChoices(options, stepId, ids), { stepId });
  const update = (id: string, patch: Partial<EventOption>) =>
    onChange(
      options.map((o) =>
        o.id === id
          ? { ...o, ...patch, ...(patch.eventType ? { category: EVENT_TYPE_CATEGORY_MAP[patch.eventType] } : {}) }
          : o,
      ),
      { structural: true },
    );
  const setStart = (id: string, on: boolean) => {
    const entries = stepChoices(options, START_STEP);
    setButtons(START_STEP, on ? [...entries, id] : entries.filter((e) => e !== id));
  };
  const toggleRequires = (opt: EventOption, id: string) => {
    const req = opt.requires ?? [];
    update(opt.id, { requires: req.includes(id) ? req.filter((r) => r !== id) : [...req, id] });
  };
  const remove = (id: string) => {
    setOpenId(null);
    onChange(removeOptionFromFlow(options, id), { structural: true });
  };
  const startAdding = () => {
    const lastOpen = openId && byId.has(openId) && !isDelayOption(byId.get(openId)!) ? openId : null;
    const lastFlow = [...flowSteps].reverse().find((o) => categoryOf(o) !== "END");
    setAdding({ name: "", eventType: "PROGRESS_UPDATE", after: lastOpen ?? lastFlow?.id ?? START_STEP });
  };
  const add = () => {
    if (!adding || !adding.name.trim()) return;
    const step: EventOption = {
      id: newOptionId(),
      displayName: adding.name.trim(),
      eventType: adding.eventType,
      category: EVENT_TYPE_CATEGORY_MAP[adding.eventType],
      isCustom: true,
    };
    const isDelay = isDelayOption(step);
    onChange(isDelay ? [...options, step] : insertStepAfter(options, step, adding.after), { structural: true });
    setAdding(null);
    setOpenId(isDelay ? null : step.id);
  };

  const chipsFor = (stepId: string) => {
    const opt = byId.get(stepId);
    if (opt && opt.next === undefined) return <span className="sf-then-note">then → guessed</span>;
    const ids = stepChoices(options, stepId).filter((id) => byId.has(id));
    if (ids.length === 0) return <span className="sf-then-note">ends the activity</span>;
    return ids.map((id, i) => (
      <span key={id} className={i === 0 ? "sf-chip sf-chip--big" : "sf-chip"}>
        {nameOf(byId.get(id))}
      </span>
    ));
  };

  const settings = (opt: EventOption) => {
    const others = flowSteps.filter((o) => o.id !== opt.id);
    return (
      <div className="sf-settings">
        {canEditSteps && (
          <div className="sf-fields">
            <label className="sf-field">
              <span>Name</span>
              <input
                className="form-input"
                value={opt.displayName}
                placeholder="Step name (e.g. Push back)"
                onChange={(e) => update(opt.id, { displayName: e.target.value })}
              />
            </label>
            <label className="sf-field">
              <span>Kind</span>
              <select
                className="form-input"
                value={currentKind(opt.eventType)}
                onChange={(e) => update(opt.id, { eventType: e.target.value as EventType })}
                title="Drives wait, delay and duration stats"
              >
                {kinds.map((t) => (
                  <option key={t} value={t}>
                    {kindLabel(t)}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
        {!isDelayOption(opt) && (
          <>
            <span className="sf-sub">Then offer</span>
            <StepButtonsEditor options={options} stepId={opt.id} onChange={(ids) => setButtons(opt.id, ids)} />
            <div className="sf-toggles">
              <label className="sf-toggle">
                <input type="checkbox" checked={opt.start === true} onChange={(e) => setStart(opt.id, e.target.checked)} />
                Can start the activity
              </label>
              <label className="sf-toggle">
                <input type="checkbox" checked={opt.once === true} onChange={(e) => update(opt.id, { once: e.target.checked })} />
                Happens once
              </label>
            </div>
            {others.length > 0 && (
              <div className="sf-needs">
                <span className="sf-sub">Needs first <span className="sf-sub-hint">held back until these are logged</span></span>
                <div className="so-chips">
                  {others.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      className={`so-chip${(opt.requires ?? []).includes(o.id) ? " so-chip--on" : ""}`}
                      aria-pressed={(opt.requires ?? []).includes(o.id)}
                      onClick={() => toggleRequires(opt, o.id)}
                    >
                      {nameOf(o)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
        {canEditSteps && (
          <button type="button" className="sf-delete" onClick={() => remove(opt.id)}>
            Delete step
          </button>
        )}
      </div>
    );
  };

  const row = (opt: EventOption) => {
    const open = openId === opt.id;
    return (
      <li key={opt.id} className={`sf-row${open ? " is-open" : ""}`}>
        <button type="button" className="sf-head" onClick={() => toggle(opt.id)} aria-expanded={open}>
          <span className="sf-name">{nameOf(opt)}</span>
          {opt.start && !isDelayOption(opt) && <span className="sf-badge">Can start</span>}
          <span className="sf-kind">{kindLabel(opt.eventType)}</span>
        </button>
        {!open && !isDelayOption(opt) && <div className="sf-then">{chipsFor(opt.id)}</div>}
        {issuesFor(opt.id).map((issue, i) => (
          <span key={i} className={`event-option-issue is-${issue.severity}`}>
            “{nameOf(opt)}” {issue.message}
          </span>
        ))}
        {open && settings(opt)}
      </li>
    );
  };

  const startOpen = openId === START_STEP;

  return (
    <div className="sf">
      {issues
        .filter((i) => i.optionId === null)
        .map((issue, i) => (
          <span key={i} className={`event-option-issue is-${issue.severity}`}>
            {issue.message}
          </span>
        ))}
      <ol className="sf-list">
        <li className={`sf-row sf-row--start${startOpen ? " is-open" : ""}`}>
          <button type="button" className="sf-head" onClick={() => toggle(START_STEP)} aria-expanded={startOpen}>
            <span className="sf-name">Start</span>
            <span className="sf-kind">First buttons</span>
          </button>
          {startOpen ? (
            <div className="sf-settings">
              <StepButtonsEditor options={options} stepId={START_STEP} onChange={(ids) => setButtons(START_STEP, ids)} />
            </div>
          ) : (
            <div className="sf-then">{chipsFor(START_STEP)}</div>
          )}
        </li>
        {flowSteps.map(row)}
      </ol>

      {delays.length > 0 && (
        <>
          <span className="sf-sub">Interruptions <span className="sf-sub-hint">offered whenever an activity is running</span></span>
          <ol className="sf-list">{delays.map(row)}</ol>
        </>
      )}

      {canEditSteps &&
        (adding ? (
          <div className="sf-add-form">
            <div className="sf-fields">
              <label className="sf-field">
                <span>Name</span>
                <input
                  className="form-input"
                  value={adding.name}
                  placeholder="e.g. Single Rider"
                  autoFocus
                  onChange={(e) => setAdding({ ...adding, name: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      add();
                    }
                  }}
                />
              </label>
              <label className="sf-field">
                <span>Kind</span>
                <select
                  className="form-input"
                  value={adding.eventType}
                  onChange={(e) => setAdding({ ...adding, eventType: e.target.value as EventType })}
                >
                  {kinds.map((t) => (
                    <option key={t} value={t}>
                      {kindLabel(t)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {!isDelayOption({ eventType: adding.eventType } as EventOption) && (
              <label className="sf-field">
                <span>Comes after</span>
                <select className="form-input" value={adding.after} onChange={(e) => setAdding({ ...adding, after: e.target.value })}>
                  <option value={START_STEP}>The start (a new first step)</option>
                  {flowSteps.map((o) => (
                    <option key={o.id} value={o.id}>
                      {nameOf(o)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <div className="sf-add-actions">
              <button type="button" className="btn btn-small" onClick={add} disabled={!adding.name.trim()}>
                Add step
              </button>
              <button type="button" className="btn-link" onClick={() => setAdding(null)}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="sf-add" onClick={startAdding}>
            + Add step
          </button>
        ))}
    </div>
  );
}
